import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiFilter, FiHeart, FiEye, FiStar } = FiIcons;

const Catalog = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [favorites, setFavorites] = useState(new Set());

  const categories = [
    { id: 'all', name: 'All Frames' },
    { id: 'men', name: 'Men' },
    { id: 'women', name: 'Women' },
    { id: 'kids', name: 'Kids' },
    { id: 'sunglasses', name: 'Sunglasses' }
  ];

  const frames = [
    {
      id: 1,
      name: 'Ray-Ban Aviator Classic',
      brand: 'Ray-Ban',
      category: 'sunglasses',
      price: 'Rs. 32,000',
      image: 'https://images.unsplash.com/photo-1511499767150-a48a237f0083?w=400&h=300&fit=crop',
      rating: 4.8,
      features: ['UV Protection', 'Polarized', 'Metal Frame']
    },
    {
      id: 2,
      name: 'Vogue Elegance Series',
      brand: 'Vogue',
      category: 'women',
      price: 'Rs. 28,500',
      image: 'https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=400&h=300&fit=crop',
      rating: 4.7,
      features: ['Lightweight', 'Acetate Frame', 'Designer']
    },
    {
      id: 3,
      name: 'Armani Exchange Professional',
      brand: 'Armani Exchange',
      category: 'men',
      price: 'Rs. 45,000',
      image: 'https://images.unsplash.com/photo-1556306535-38febf6782e7?w=400&h=300&fit=crop',
      rating: 4.9,
      features: ['Titanium', 'Anti-glare', 'Business Style']
    },
    {
      id: 4,
      name: 'Elle Fashion Forward',
      brand: 'Elle',
      category: 'women',
      price: 'Rs. 18,500',
      image: 'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400&h=300&fit=crop',
      rating: 4.6,
      features: ['Trendy Design', 'Comfortable Fit', 'Durable']
    },
    {
      id: 5,
      name: 'Kids Rainbow Collection',
      brand: 'Mahanama Kids',
      category: 'kids',
      price: 'Rs. 8,500',
      image: 'https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=400&h=300&fit=crop',
      rating: 4.5,
      features: ['Flexible Frame', 'Safe Materials', 'Fun Colors']
    },
    {
      id: 6,
      name: 'Che Classic Business',
      brand: 'Che',
      category: 'men',
      price: 'Rs. 15,000',
      image: 'https://images.unsplash.com/photo-1473496169904-658ba7c44d8a?w=400&h=300&fit=crop',
      rating: 4.4,
      features: ['Professional Look', 'Scratch Resistant', 'Affordable']
    }
  ];

  const filteredFrames = activeFilter === 'all' 
    ? frames 
    : frames.filter(frame => frame.category === activeFilter);

  const toggleFavorite = (frameId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(frameId)) {
      newFavorites.delete(frameId);
    } else {
      newFavorites.add(frameId);
    }
    setFavorites(newFavorites);
  };

  return (
    <section id="catalog" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Frame <span className="gradient-text">Collections</span>
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Discover our premium selection of eyewear from world-renowned brands
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <motion.button
              key={category.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveFilter(category.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeFilter === category.id
                  ? 'glass-button-primary text-white'
                  : 'glass-button text-white/70 hover:text-white'
              }`}
            >
              {category.name}
            </motion.button>
          ))}
        </motion.div>

        {/* Frames Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredFrames.map((frame, index) => (
            <motion.div
              key={frame.id}
              layout
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="glass-card overflow-hidden group"
            >
              <div className="relative">
                <img
                  src={frame.image}
                  alt={frame.name}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => toggleFavorite(frame.id)}
                  className={`absolute top-4 right-4 p-2 rounded-full backdrop-blur-md transition-colors ${
                    favorites.has(frame.id)
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-white/10 text-white/70 hover:text-red-400'
                  }`}
                >
                  <SafeIcon icon={FiHeart} className="text-lg" />
                </motion.button>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-blue-400 font-medium">
                    {frame.brand}
                  </span>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiStar} className="text-yellow-400 text-sm" />
                    <span className="text-white/70 text-sm">{frame.rating}</span>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-2">
                  {frame.name}
                </h3>

                <p className="text-2xl font-bold text-blue-400 mb-4">
                  {frame.price}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {frame.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-white/10 text-white/70 text-xs rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 glass-button-primary py-3 text-white font-medium"
                  >
                    Try In Store
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="glass-button px-4 py-3 text-white"
                  >
                    <SafeIcon icon={FiEye} className="text-lg" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-16"
        >
          <p className="text-white/70 mb-6">
            Can't find what you're looking for? We have many more options in store!
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="glass-button px-8 py-4 text-white font-medium"
          >
            Visit Our Store
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Catalog;